import { GridItemChangeEvent } from '@progress/kendo-react-grid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Accordion from 'src/components/design-system/Accordion';
import useDebouncedValue from 'src/hooks/useDebouncedValue';
import { warningIcon } from 'src/utils/icons';
import { useContextSelector } from 'use-context-selector';
import { generateGlCodesFromLineItems } from './generateGlCodesFromLineItems';
import GLDistributionTable from './GLDistributionTable';
import { InvoiceContext } from './InvoiceDetailsContext';
import { calculateTotals } from './useGlDistributionsGrandTotals';

interface CalcTotalParams {
  discountAmt: number;
  salesTaxAmt: number;
  shipCostAmt: number;
  glSubTotal: number;
}

const calculateTotal = ({ discountAmt, salesTaxAmt, shipCostAmt, glSubTotal }: CalcTotalParams) =>
  (glSubTotal || 0) +
  (typeof shipCostAmt === 'string' && shipCostAmt === '' ? 0 : Number(shipCostAmt) || 0) +
  (typeof salesTaxAmt === 'string' && salesTaxAmt === '' ? 0 : Number(salesTaxAmt) || 0) -
  (typeof discountAmt === 'string' && discountAmt === '' ? 0 : Number(discountAmt) || 0);

const InvoiceDetailsGLDistribution = () => {
  const discountAmt = useContextSelector(InvoiceContext, (value) => value?.invoice?.discountAmt);
  const shipCostAmt = useContextSelector(InvoiceContext, (value) => value?.invoice?.shipCostAmt);
  const salesTaxAmt = useContextSelector(InvoiceContext, (value) => value?.invoice?.salesTaxAmt);
  const poNum = useContextSelector(InvoiceContext, (value) => value?.invoice?.poNum);
  const setAreLineItemsDebouncing = useContextSelector(
    InvoiceContext,
    (value) => value?.setAreLineItemsDebouncing
  );

  const isAutoDistGlBreakout = useContextSelector(
    InvoiceContext,
    (value) => value?.invoice?.isAutoDistGlBreakout
  );
  const invoiceNumber = useContextSelector(
    InvoiceContext,
    (value) => value?.invoice?.invoiceNumber
  );
  const title = useContextSelector(InvoiceContext, (value) => value.title);
  const glCodesObject = useContextSelector(InvoiceContext, (value) => value.glCodesObject);
  const invoiceLineItems = useContextSelector(InvoiceContext, (value) => value.invoiceLineItems);
  const setInvoiceGlCodes = useContextSelector(InvoiceContext, (value) => value.setInvoiceGlCodes);
  const hasGLChanged = useContextSelector(InvoiceContext, (value) => value.hasGLChanged);
  const initialInvoiceGlBreakouts = useContextSelector(
    InvoiceContext,
    (value) => value.initialInvoice?.glBreakouts || []
  );

  const lineItemsWithoutTexts = useMemo(
    () =>
      invoiceLineItems.map((i) => ({
        ...i,
        description: undefined as unknown as string,
        productItemNum: undefined as unknown as string,
        uom: undefined as unknown as string,
      })),
    [invoiceLineItems]
  );

  const debouncedLineItems = useDebouncedValue(
    lineItemsWithoutTexts,
    500,
    setAreLineItemsDebouncing
  );

  const isInitiallyLoading = useContextSelector(
    InvoiceContext,
    (value) => value.isInitiallyLoading
  );
  const isInvoiceMPA = useContextSelector(InvoiceContext, (value) => value.isInvoiceMPA);
  const invoiceGlCodes = useContextSelector(InvoiceContext, (value) => value.invoiceGlCodes);
  const expandedGLCode = useContextSelector(InvoiceContext, (value) => value.expandedGLCode);

  const handleItemChange = useCallback(
    (e: GridItemChangeEvent) => {
      const { rowId } = e.dataItem;
      const { field = '', value } = e;

      setInvoiceGlCodes((prev) => {
        const data = [...prev].map((item) => ({
          ...item,
          ...(rowId === item.rowId && {
            [field]: value,

            ...(field === 'glCode' && {
              glCodeId: glCodesObject[value].id,
            }),

            ...((field === 'discountAmt' || field === 'salesTaxAmt' || field === 'shipCostAmt') && {
              glTotal: calculateTotal({
                ...item,
                glSubTotal: item?.glSubTotal || 0,
                [field]: Number(value || 0),
              }),
            }),
          }),
        }));

        if (field === 'discountAmt' || field === 'salesTaxAmt' || field === 'shipCostAmt') {
          return [...data.slice(0, -1), calculateTotals(data.slice(0, -1))];
        }

        return data;
      });
    },
    [glCodesObject, setInvoiceGlCodes]
  );

  const updateInvoiceGLCodes = useCallback(async () => {
    if (!isInitiallyLoading && glCodesObject && Object.keys(glCodesObject).length) {
      setInvoiceGlCodes((prev) => {
        if (poNum == null || prev.length) {
          const { glCodes } = generateGlCodesFromLineItems(
            debouncedLineItems,
            Number(discountAmt || 0),
            Number(shipCostAmt || 0),
            Number(salesTaxAmt || 0),
            glCodesObject,
            prev.slice(0, -1),
            isAutoDistGlBreakout,
            initialInvoiceGlBreakouts,
            hasGLChanged
          );
          glCodes.sort((a, b) => a.glCodeId - b.glCodeId);
          const oldGlCodes = glCodes.filter((code) => !code.isNewlyAdded);
          const newGlCodes = glCodes.filter((code) => code.isNewlyAdded);
          return [...oldGlCodes, ...newGlCodes, calculateTotals(glCodes)];
        }

        return prev;
      });
    }
  }, [
    isInitiallyLoading,
    glCodesObject,
    setInvoiceGlCodes,
    poNum,
    debouncedLineItems,
    discountAmt,
    shipCostAmt,
    salesTaxAmt,
    isAutoDistGlBreakout,
    initialInvoiceGlBreakouts,
    hasGLChanged,
  ]);

  useEffect(() => {
    updateInvoiceGLCodes();
  }, [updateInvoiceGLCodes]);

  const [expanded, setExpanded] = useState(true);

  return (
    <Accordion
      title={
        <p className="flex items-center gap-3">
          GL Distribution Summary for {title} #{invoiceNumber || ''}
          {isInvoiceMPA && (
            <span className="flex items-center gap-1 p-1 bg-white text-xs text-black font-normal rounded">
              {warningIcon} Multiple Periods Assigned
            </span>
          )}
        </p>
      }
      defaultExpanded
      controlled
      expanded={expanded}
      setExpanded={setExpanded}
    >
      <GLDistributionTable
        dataSource={invoiceGlCodes.map((gl) => ({
          ...gl,
          expanded: expandedGLCode.includes(gl.glCode as string),
        }))}
        onItemChange={handleItemChange}
        DataItemKey="glCode"
      />
    </Accordion>
  );
};

export default InvoiceDetailsGLDistribution;
